import type { NotificationPermissionStatus } from "../../../services/notificationPermissionStatus";

/**
 * What one tap on the push switch should do.
 *
 *  - `wait` — the permission has not loaded yet.
 *  - `ask-os` — the OS has never been asked; show its prompt.
 *  - `open-os-settings` — the OS refused, and only its own screen can undo that.
 *  - `explain-os-settings` — the same, on a browser we cannot deep-link into.
 *  - `set-account` — the OS already allows push, so the only thing left to
 *    decide is the account, and the tap decides it.
 */
export type PushSwitchAction =
  | { kind: "wait" }
  | { kind: "ask-os" }
  | { kind: "open-os-settings" }
  | { kind: "explain-os-settings" }
  | { kind: "set-account"; pushOptOut: boolean };

/**
 * The reader's own push decision, or the fact that we do not have it yet.
 *
 * `loaded: false` is NOT the same as `pushOptOut: null`. Null is an answer of a
 * kind — none has been recorded, so nothing is suppressed. Unloaded is the
 * absence of knowledge, and the two were conflated: `/users/me` in flight or
 * failed gives `currentUser === null`, which read as "not opted out", so the
 * switch claimed ON and a tap on it recorded a refusal the reader never made.
 */
export type AccountPushDecision =
  { loaded: false } | { loaded: true; pushOptOut: boolean | null };

/**
 * Whether the reader is actually going to receive a push.
 *
 * Both gates have to agree, and they answer different questions: the OS decides
 * whether this device may display one, `pushOptOut` decides whether the reader
 * wants any at all. The switch showed only the first, so an opted-out reader on
 * an authorized device saw a switch reading ON while nothing ever arrived.
 *
 * An unloaded account is not a third answer: we cannot say push is on, so we do
 * not say it.
 */
export function isPushOn(
  osStatus: NotificationPermissionStatus | null,
  account: AccountPushDecision
): boolean {
  if (!account.loaded) return false;
  return osStatus === "authorized" && account.pushOptOut !== true;
}

/**
 * The switch is the only control that can state an account-wide decision, so
 * once the OS is out of the way it writes one.
 *
 * `pushPermissionAnswer` deliberately records nothing from an ambient reading:
 * a device that merely finds itself `authorized` must not clear an opt-out the
 * reader made elsewhere. That leaves a reader who has opted out, and whose
 * every device already allows push, with no transition left to witness and so
 * no way back. This is the way back. It is also the first way to opt out
 * WITHOUT revoking the OS permission — which on the web meant a trip into
 * browser settings, and on a phone meant losing push from every app at once.
 *
 * While the OS has not granted permission the tap still belongs to the OS:
 * there is nothing to gain from recording a wish for push on a device that
 * cannot display one, and the permission flow records the reader's answer by
 * itself once it resolves (`pushPermissionAnswer` sees the change).
 */
export function pushSwitchAction(
  osStatus: NotificationPermissionStatus | null,
  account: AccountPushDecision,
  isNative: boolean
): PushSwitchAction {
  if (osStatus === null) return { kind: "wait" };
  // Asking the OS writes nothing to the account, so it does not need one — and
  // making it wait would strand a first-time opt-in behind a slow `/users/me`.
  if (osStatus === "notDetermined") return { kind: "ask-os" };
  if (osStatus === "denied")
    return isNative
      ? { kind: "open-os-settings" }
      : { kind: "explain-os-settings" };
  // `authorized`: the OS is satisfied, so the tap is about the account, and it
  // flips whatever the reader is on now — which means it has to know what that
  // is. Reading an unloaded account as "not opted out" flipped it to a refusal
  // nobody asked for.
  if (!account.loaded) return { kind: "wait" };
  return { kind: "set-account", pushOptOut: account.pushOptOut !== true };
}
